import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { PiFileCsv, PiArrowSquareOut } from "react-icons/pi";
import AddBoltImage from '@/assets/visuals/add-bolt.webp';

import Dropzone from "@/components/common/Dropzone/Dropzone";
import FileScanner from "@/components/common/Dropzone/FileScanner";
import MotionText from "@/components/common/Motion/MotionText";
import MotionDiv from "@/components/common/Motion/MotionDiv";
import HeaderBack from "@/components/dashboard/Header/HeaderBack";

import ProgressBar from "@/components/common/ProgressBar/ProgressBar";

import { useKycStore } from "@/store/useKycStore";
import { useBoltStore } from "@/store/useBoltStore";
import { usePageSetup } from "@/hooks/usePageSetup";
import { parseCSV } from "@/utils/csv";

export const BoltTripsPage = () => {
  const { t } = useTranslation()
  usePageSetup(t('bolt.pageTitle.trips'))
  /* Reusing KYC */
  const progress = useKycStore((state) => state.progress)
  const setProgress = useKycStore((state) => state.setProgress)

  useEffect(() => setProgress(2), [])

  const navigate = useNavigate();

  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setTripAnalysis = useBoltStore((state) => state.setTripAnalysis)

  /* We can easily parse the CSV file in frontend */
  const analyzeFile = async (file: File) => {
    try {
      const text = await file.text();
      const rows = parseCSV(text);

      const dataRows = rows.slice(1);

      const line_items = dataRows
        .map(row => ({
          reference: row[0],
          date: row[1].split(' ')[0].split('.').reverse().join('-'),
          amount: parseFloat(row[15])
        }))
        .filter(item => item.reference && !isNaN(item.amount));

      if (line_items.length === 0) throw new Error('Empty CSV');

      return { line_items };
    } catch (error) {
      console.log(error)
      setError(t('bolt.errors.fileUploadFailed'));
      setIsScanning(false);
      return null;
    }
  };

  const handleFileSelect = async (file: File) => {
    setIsScanning(true);
    setError(null);

    const tripAnalysis = await analyzeFile(file);

    if (tripAnalysis) {
      setTripAnalysis(tripAnalysis.line_items)
      navigate("/bolt/income");
    }
  };

  return (
    <div className="h-full bg-white flex flex-col">
      <HeaderBack>
        <div className="flex flex-row items-center w-[8.5rem] ml-[3.84rem]">
          <ProgressBar
            currentStep={progress}
            totalSteps={3}
          />
        </div>
      </HeaderBack>
      <div className="h-full flex flex-col px-5 pb-[10rem]">
        <MotionText
          as="h2"
          className="font-semibold text-3xl text-zinc-900 mb-3"
          delay={0.1}
        >
          {t('bolt.trips.title')}
        </MotionText>
        <MotionText
          as="p"
          className="text-zinc-700 text-sm mb-2"
          delay={0.2}
        >
          {t('bolt.trips.description')}
        </MotionText>
        <MotionText
          as="p"
          className="text-zinc-700 text-xs mb-8"
          delay={0.2}
        >
          <Link className="underline" to="/bolt/trips/how">{t('bolt.trips.howToFind')}</Link>
        </MotionText>
        <MotionDiv
          className="bg-gray-100 p-4 rounded-lg flex flex-row gap-2 mb-4"
          delay={0.2}
        >
          <PiFileCsv className="h-6 w-6 text-black shrink-0" />
          <Link className="text-sm text-zinc-700 font-semibold flex-1 flex items-center gap-1" target="_blank" to="https://partners.bolt.eu/rider-invoices">
            {t('bolt.trips.downloadCsv')} <span className="underline text-blue-600 hover:text-blue-700 inline-flex items-center gap-0.5">Bolt <PiArrowSquareOut className="w-4 h-4" /></span>
          </Link>
        </MotionDiv>
        {error && (
          <MotionText
            as="p"
            className="text-red-500 text-sm"
            delay={0.2}
          >
            {error}
          </MotionText>
        )}
        <MotionDiv
          className="flex flex-col gap-2"
          delay={0.3}
        >
          {isScanning ? (
            <FileScanner isScanning={isScanning} />
          ) : (
            <Dropzone
              type="csv"
              onFileSelect={handleFileSelect}
              maxSize={10 * 1024 * 1024}
              buttonText={t('common.selectFile')}
            >
              <img src={AddBoltImage}
                className='w-[4.375rem] h-[5.75rem] mb-2'
                alt="Add Bolt"
              />
            </Dropzone>
          )}
        </MotionDiv>
      </div>
    </div>
  );
};

export default BoltTripsPage;